import React, { useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  useTheme,
  alpha,
  LinearProgress,
} from "@mui/material";
import { MdCorporateFare } from "react-icons/md";

const AttendanceQuickView = ({
  attendanceRecords = [],
  totalEmployees = 0,
  loading = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const todayStr = useMemo(() => new Date().toISOString().split("T")[0], []);

  // Filter today's attendance records
  const todaysAttendance = useMemo(() => {
    return attendanceRecords.filter((r) => {
      const recDate = (r.date || r.check_in || r.created_at || "").split("T")[0];
      return !recDate || recDate === todayStr;
    }).sort((a, b) => {
      const timeA = new Date(b.check_in || b.check_out || b.created_at || 0).getTime();
      const timeB = new Date(a.check_in || a.check_out || a.created_at || 0).getTime();
      return timeA - timeB;
    });
  }, [attendanceRecords, todayStr]);

  // Stats Calculations
  const inOfficeCount = todaysAttendance.filter((r) => r.check_in && !r.check_out).length;
  const presentTodayCount = todaysAttendance.filter((r) => r.check_in).length;
  const totalStaff = totalEmployees || (todaysAttendance.length > 0 ? todaysAttendance.length : 1);

  const presentPercentageToday = useMemo(() => {
    if (totalStaff === 0) return 0;
    return Math.min(100, Math.round((presentTodayCount / totalStaff) * 100));
  }, [presentTodayCount, totalStaff]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.25 },
        borderRadius: "8px",
        bgcolor: isDark ? "#111827" : "#ffffff",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"}`,
        boxShadow: isDark
          ? "0 1px 3px rgba(0,0,0,0.4)"
          : "0 1px 3px rgba(15,23,42,0.03)",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "6px",
              bgcolor: isDark ? alpha("#4f46e5", 0.15) : "#eef2ff",
              color: "#4f46e5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <MdCorporateFare size={22} />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                fontSize: "0.9rem",
                lineHeight: 1.2,
              }}
            >
              HQ Floor Desk Occupancy
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "0.75rem",
                display: "block",
                mt: 0.25,
              }}
            >
              Main Building • {inOfficeCount} Active In-Office ({presentTodayCount}/{totalStaff} Present)
            </Typography>
          </Box>
        </Stack>

        {/* Metric on the right */}
        <Box sx={{ textAlign: "right", flexShrink: 0 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              color: "text.primary",
              fontSize: "1.1rem",
              lineHeight: 1.1,
            }}
          >
            {presentPercentageToday}%{" "}
            <Typography
              component="span"
              variant="caption"
              sx={{ color: "text.secondary", fontWeight: 600, fontSize: "0.75rem" }}
            >
              Full
            </Typography>
          </Typography>
          <Box sx={{ width: 80, mt: 0.5 }}>
            <LinearProgress
              variant="determinate"
              value={presentPercentageToday}
              sx={{
                height: 4,
                borderRadius: "2px",
                bgcolor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                "& .MuiLinearProgress-bar": {
                  borderRadius: "2px",
                  bgcolor: "#4f46e5",
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default AttendanceQuickView;
